<template>
  <div ref="dropdownRef" class="dropdown">
    <div
      class="dropdown-trigger"
      :class="{ 'dropdown-trigger-active': isOpen }"
      @click="toggle"
      @keydown.enter="toggle"
      @keydown.space.prevent="toggle"
      @keydown.escape="close"
      @keydown.arrow-down.prevent="open"
      :aria-expanded="isOpen"
      :aria-haspopup="true"
      :aria-label="ariaLabel"
      :disabled="disabled"
      tabindex="0"
      role="button"
    >
      <slot name="trigger">
        <span class="dropdown-label">{{ selectedLabel || placeholder }}</span>
        <Icon
          name="chevron-down"
          :size="12"
          class="dropdown-arrow"
          :class="{ 'dropdown-arrow-open': isOpen }"
        />
      </slot>
    </div>
    
    <div
      v-if="isOpen"
      class="dropdown-menu"
      :class="[`dropdown-menu-${position}`, menuClass]"
      :aria-label="menuAriaLabel"
      role="listbox"
    >
      <div
        v-for="(option, index) in options"
        :key="getOptionKey(option, index)"
        class="dropdown-item"
        :class="{
          'dropdown-item-selected': isSelected(option),
          'dropdown-item-highlighted': highlightedIndex === index,
          'dropdown-item-disabled': isOptionDisabled(option)
        }"
        @click="selectOption(option)"
        @mouseenter="highlightedIndex = index"
        :role="'option'"
        :aria-selected="isSelected(option)"
        :aria-disabled="isOptionDisabled(option)"
      >
        <Icon
          v-if="option.icon"
          :name="option.icon"
          :size="16"
          class="dropdown-item-icon"
        />
        <span class="dropdown-item-label">{{ getOptionLabel(option) }}</span>
        <span
          v-if="option.description"
          class="dropdown-item-description"
        >
          {{ option.description }}
        </span>
        <Icon
          v-if="isSelected(option)"
          name="check"
          :size="12"
          class="dropdown-item-check"
        />
      </div>
      
      <div v-if="options.length === 0" class="dropdown-empty">
        {{ emptyText }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import Icon from './Icon.vue'

export interface DropdownOption {
  value: any
  label?: string
  icon?: string
  description?: string
  disabled?: boolean
}

export interface DropdownProps {
  options: DropdownOption[]
  modelValue?: any
  placeholder?: string
  disabled?: boolean
  position?: 'bottom' | 'top'
  menuClass?: string
  ariaLabel?: string
  menuAriaLabel?: string
  emptyText?: string
  keyField?: string
  labelField?: string
  valueField?: string
}

const props = withDefaults(defineProps<DropdownProps>(), {
  placeholder: 'Select an option',
  position: 'bottom',
  emptyText: 'No options available',
  keyField: 'value',
  labelField: 'label',
  valueField: 'value'
})

const emit = defineEmits<{
  'update:modelValue': [value: any]
  'change': [option: DropdownOption | null]
  'open': []
  'close': []
}>()

const dropdownRef = ref<HTMLElement>()
const isOpen = ref(false)
const highlightedIndex = ref(-1)

const selectedOption = computed(() => {
  if (!props.options || !Array.isArray(props.options)) return undefined
  return props.options.find(option => getOptionValue(option) === props.modelValue)
})

const selectedLabel = computed(() => {
  return selectedOption.value ? getOptionLabel(selectedOption.value) : ''
})

const getOptionKey = (option: DropdownOption, index: number): string => {
  const key = option[props.keyField as keyof DropdownOption]
  return key !== undefined ? String(key) : String(index)
}

const getOptionLabel = (option: DropdownOption): string => {
  const label = option[props.labelField as keyof DropdownOption]
  return label !== undefined ? String(label) : String(option.value)
}

const getOptionValue = (option: DropdownOption): any => {
  const value = option[props.valueField as keyof DropdownOption]
  return value !== undefined ? value : option.value
}

const isSelected = (option: DropdownOption): boolean => {
  return getOptionValue(option) === props.modelValue
}

const isOptionDisabled = (option: DropdownOption): boolean => {
  return option.disabled === true
}

const open = (): void => {
  if (props.disabled) return
  
  isOpen.value = true
  highlightedIndex.value = -1
  emit('open')
}

const close = (): void => {
  isOpen.value = false
  highlightedIndex.value = -1
  emit('close')
}

const toggle = (): void => {
  if (isOpen.value) {
    close()
  } else {
    open()
  }
}

const selectOption = (option: DropdownOption): void => {
  if (isOptionDisabled(option)) return
  
  const value = getOptionValue(option)
  emit('update:modelValue', value)
  emit('change', option)
  close()
}

const handleClickOutside = (event: MouseEvent) => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target as Node)) {
    close()
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped lang="scss">

.dropdown {
  position: relative;
  display: inline-block;
  min-width: 120px;
}

.dropdown-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 8px;
  background: var(--vscode-dropdown-background);
  color: var(--vscode-dropdown-foreground);
  border: 1px solid var(--vscode-dropdown-border);
  border-radius: 2px;
  cursor: pointer;
  font-size: 13px;
  line-height: 18px;
  min-height: 26px;
  
  &:hover:not(:disabled) {
    background: var(--vscode-dropdown-background);
    border-color: var(--vscode-inputOption-activeBorder);
  }
  
  &:focus {
    outline: none;
    border-color: var(--vscode-focusBorder);
    box-shadow: 0 0 0 1px var(--vscode-focusBorder);
  }
  
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  
  &.dropdown-trigger-active {
    background: var(--vscode-dropdown-background);
    border-color: var(--vscode-focusBorder);
  }
}

.dropdown-label {
  flex: 1;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dropdown-arrow {
  margin-left: 4px;
  color: var(--vscode-foreground);
  transition: transform 0.15s ease;
  
  &.dropdown-arrow-open {
    transform: rotate(180deg);
  }
}

.dropdown-menu {
  position: absolute;
  z-index: 1000;
  left: 0;
  right: 0;
  background: var(--vscode-dropdown-listBackground);
  border: 1px solid var(--vscode-widget-border);
  border-radius: 2px;
  box-shadow: 0 2px 8px var(--vscode-widget-shadow);
  max-height: 300px;
  overflow-y: auto;
  
  &.dropdown-menu-bottom {
    top: 100%;
    margin-top: 1px;
  }
  
  &.dropdown-menu-top {
    bottom: 100%;
    margin-bottom: 1px;
  }
}

.dropdown-item {
  display: flex;
  align-items: center;
  padding: 4px 8px;
  cursor: pointer;
  font-size: 13px;
  line-height: 18px;
  color: var(--vscode-foreground);
  
  &:hover:not(.dropdown-item-disabled) {
    background: var(--vscode-list-hoverBackground);
    color: var(--vscode-list-hoverForeground);
  }
  
  &.dropdown-item-highlighted:not(.dropdown-item-disabled) {
    background: var(--vscode-list-focusBackground);
    color: var(--vscode-list-focusForeground);
  }
  
  &.dropdown-item-selected {
    background: var(--vscode-list-activeSelectionBackground);
    color: var(--vscode-list-activeSelectionForeground);
  }
  
  &.dropdown-item-disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.dropdown-item-icon {
  margin-right: 6px;
  flex-shrink: 0;
}

.dropdown-item-label {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dropdown-item-description {
  margin-left: 8px;
  opacity: 0.7;
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dropdown-item-check {
  margin-left: 6px;
  flex-shrink: 0;
  color: var(--vscode-list-activeSelectionIconForeground);
}

.dropdown-empty {
  padding: 8px;
  text-align: center;
  color: var(--vscode-descriptionForeground);
  font-size: 12px;
  font-style: italic;
}
</style>
